import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BackButton } from '../components/BackButton';
import { HCaptcha } from '../components/HCaptcha';
import { APP_CONFIG } from '../config/constants';

interface SimpleSignupPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const SimpleSignupPage: React.FC<SimpleSignupPageProps> = ({ showToast }) => {
  const navigate = useNavigate();
  const { signUp, signOut, loading, user } = useAuth();
  
  const [captchaToken, setCaptchaToken] = useState<string>('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    // Informations de profil
    role: '' as 'etudiant' | 'repetiteur' | 'les_deux' | '',
    filiere: '',
    annee: '',
    telephone: '',
    // Pour tuteurs
    tarif: 30,
    matieres: [] as string[]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Rediriger si l'utilisateur est déjà connecté
  useEffect(() => {
    if (!loading && user) {
      navigate('/profile');
    }
  }, [user, loading, navigate]);

  const filieres = [
    { value: 'IG', label: 'Informatique de Gestion (IG)' },
    { value: 'EE', label: 'Économie d\'Entreprise (EE)' },
    { value: 'IBM', label: 'International Business Management (IBM)' },
    { value: 'IS', label: 'Information Science (IS)' },
    { value: 'Autre', label: 'Autre' }
  ];

  const annees = [
    { value: '1', label: 'Année académique : 1' },
    { value: '2', label: 'Année académique : 2' },
    { value: '3', label: 'Année académique : 3' }
  ];

  // Matières par filière
  const subjectsByFiliere: Record<string, string[]> = {
    IBM: ['General Management', 'Accounting & Finance', 'Marketing & Communication', 'Leadership & Soft-skills', 'Economics', 'Business Law', 'Business Tools', 'Mathématiques'],
    IG: ['Programmation', 'Base de données', 'Réseaux & Sécurité', 'Gestion de projet', 'Analyse & Modélisation', 'Web Development', 'Mathématiques'],
    IS: ['Bibliothéconomie', 'Archivistique', 'Technologies Web', 'UX Design', 'Gestion de données', 'Recherche d\'information', 'Mathématiques'],
    EE: ['Management', 'Comptabilité', 'Finance', 'Marketing', 'Ressources Humaines', 'Economics', 'Droit des affaires', 'Mathématiques']
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubjectToggle = (subject: string) => {
    setFormData(prev => ({
      ...prev,
      matieres: prev.matieres.includes(subject)
        ? prev.matieres.filter(s => s !== subject)
        : [...prev.matieres, subject]
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      showToast('Veuillez entrer votre prénom', 'error');
      return false;
    }

    if (!formData.lastName.trim()) {
      showToast('Veuillez entrer votre nom', 'error');
      return false;
    }

    if (!formData.email) {
      showToast('Veuillez entrer votre adresse email', 'error');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showToast('Veuillez entrer une adresse email valide', 'error');
      return false;
    }

    // Vérifier que l'email est un email institutionnel HEG/HES-SO
    const emailLower = formData.email.toLowerCase();
    const isValidDomain = APP_CONFIG.validEmailDomains.some(domain => emailLower.endsWith(domain));
    
    if (!isValidDomain) {
      showToast('Veuillez utiliser votre adresse email étudiant-e (@etu.hesge.ch)', 'error');
      return false;
    }

    if (!formData.password) {
      showToast('Veuillez entrer un mot de passe', 'error');
      return false;
    }

    if (formData.password.length < 8) {
      showToast('Le mot de passe doit contenir au moins 8 caractères', 'error');
      return false;
    }

    // Validation mot de passe fort (requis par Supabase)
    if (!/[a-z]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins une minuscule (a-z)', 'error');
      return false;
    }

    if (!/[A-Z]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins une majuscule (A-Z)', 'error');
      return false;
    }

    if (!/[0-9]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins un chiffre (0-9)', 'error');
      return false;
    }

    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password)) {
      showToast('Le mot de passe doit contenir au moins un caractère spécial (!@#$...)', 'error');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      showToast('Les mots de passe ne correspondent pas', 'error');
      return false;
    }

    if (!formData.acceptTerms) {
      showToast('Vous devez accepter les conditions d\'utilisation', 'error');
      return false;
    }

    // Les autres informations (rôle, filière, année, matières, etc.) 
    // seront renseignées dans CreateProfilePage après confirmation email

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!captchaToken) {
      showToast('Veuillez valider le captcha de sécurité', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // 1. Créer le compte utilisateur seulement
      const { user, error } = await signUp(
        formData.email, 
        formData.password, 
        {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          captchaToken, // Protection anti-bot
        }
      );

      if (error) {
        
        // Messages d'erreur en français
        let errorMessage = 'Erreur lors de l\'inscription';
        
        switch (error.message) {
          case 'User already registered':
            errorMessage = 'Un compte existe déjà avec cette adresse email';
            break;
          case 'Password should be at least 6 characters':
            errorMessage = 'Le mot de passe doit contenir au moins 6 caractères';
            break;
          case 'Signup requires a valid password':
            errorMessage = 'Veuillez entrer un mot de passe valide';
            break;
          case 'Invalid email':
            errorMessage = 'Adresse email invalide';
            break;
          case 'For security purposes, you can only request this after':
            errorMessage = 'Trop de tentatives. Veuillez réessayer dans quelques minutes';
            break;
          default:
            // Gérer l'erreur de mot de passe faible de Supabase
            if (error.message.includes('Password should contain at least one character')) {
              errorMessage = 'Le mot de passe doit contenir : minuscules, majuscules, chiffres ET caractères spéciaux (!@#$...)';
            } else {
              errorMessage = error.message;
            }
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      if (user) {
        
        // 🔒 CRITIQUE : Supabase crée une session automatiquement après signUp
        // On DOIT se déconnecter immédiatement pour forcer la confirmation d'email
        await signOut();
        
        showToast('Compte créé ! Vérifiez votre email pour confirmer votre inscription avant de vous connecter.', 'success');
        
        // Rediriger vers la page de login (pas vers email-not-confirmed car plus de session)
        navigate('/login', { 
          state: { 
            message: 'Vérifiez votre email et confirmez votre inscription avant de vous connecter.' 
          } 
        });
        return;
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Si l'utilisateur est déjà connecté, ne pas afficher le formulaire
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Redirection en cours...</p>
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-user-plus text-white text-2xl"></i>
            </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Créer votre profil
          </h2>
          <p className="text-gray-600">
            Rejoignez la communauté et créez votre profil complet
          </p>
          </div>

          {/* Formulaire */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Prénom */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-user text-[#002c46] mr-2"></i>
                  Prénom *
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  placeholder="Votre prénom"
                  disabled={isSubmitting}
                />
              </div>

              {/* Nom */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-user text-[#002c46] mr-2"></i>
                  Nom *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  placeholder="Votre nom"
                  disabled={isSubmitting}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-envelope text-[#002c46] mr-2"></i>
                  Adresse email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  placeholder="prenom.nom@etu.hesge.ch"
                  disabled={isSubmitting}
                />
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-gray-700">
                    <i className="fas fa-graduation-cap text-green-600 mr-1"></i>
                    <strong>Utilise ton adresse email étudiant-e HEG :</strong>
                  </p>
                  <p className="text-xs text-gray-600 mt-1 ml-5">
                    • Format : (prénom).(nom)@etu.hesge.ch
                  </p>
                </div>
              </div>

              {/* Mot de passe */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-lock text-[#002c46] mr-2"></i>
                  Mot de passe *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                    placeholder="••••••••"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    disabled={isSubmitting}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    <i className="fas fa-shield-alt text-[#002c46] mr-1"></i>
                    Votre mot de passe doit contenir :
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 ml-5">
                    <li className={formData.password.length >= 8 ? 'text-green-600' : ''}>
                      <i className={`fas ${formData.password.length >= 8 ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Au moins 8 caractères
                    </li>
                    <li className={/[a-z]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[a-z]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Une minuscule (a-z)
                    </li>
                    <li className={/[A-Z]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[A-Z]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Une majuscule (A-Z)
                    </li>
                    <li className={/[0-9]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[0-9]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Un chiffre (0-9)
                    </li>
                    <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password) ? 'text-green-600' : ''}>
                      <i className={`fas ${/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password) ? 'fa-check-circle text-green-600' : 'fa-circle text-gray-400'} mr-1 text-xs`}></i>
                      Un caractère spécial (!@#$...)
                    </li>
                  </ul>
                </div>
              </div>

              {/* Confirmation mot de passe */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-lock text-[#002c46] mr-2"></i>
                  Confirmer le mot de passe *
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                    placeholder="••••••••"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    disabled={isSubmitting}
                  >
                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
              </div>

              {/* Message informatif - les détails seront demandés après confirmation */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <i className="fas fa-info-circle text-blue-600 mr-2"></i>
                  Après confirmation de votre email, vous pourrez compléter votre profil avec votre filière, vos matières et vos disponibilités
                </p>
              </div>

              {/* Sections masquées - seront demandées dans CreateProfilePage */}
              {false && (
              <>
              {/* Choix du rôle */}
              <div>
                <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="fas fa-user-cog text-[#002c46] mr-2"></i>
                  Je suis... *
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                >
                  <option value="">Choisissez votre rôle</option>
                  <option value="etudiant">Étudiant-e (je cherche de l'aide)</option>
                  <option value="repetiteur">Tuteur-trice (j'aide les autres)</option>
                  <option value="les_deux">Les deux (j'aide et je cherche de l'aide)</option>
                </select>
              </div>

              {/* Informations académiques */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="filiere" className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-university text-[#002c46] mr-2"></i>
                    Filière *
                  </label>
                  <select
                    id="filiere"
                    name="filiere"
                    required
                    value={formData.filiere}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  >
                    <option value="">Choisissez votre filière</option>
                    {filieres.map(filiere => (
                      <option key={filiere.value} value={filiere.value}>
                        {filiere.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="annee" className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-calendar text-[#002c46] mr-2"></i>
                    Année d'études *
                  </label>
                  <select
                    id="annee"
                    name="annee"
                    required
                    value={formData.annee}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  >
                    <option value="">Choisissez votre année</option>
                    {annees.map(annee => (
                      <option key={annee.value} value={annee.value}>
                        {annee.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Matières - pour tuteurs seulement */}
              {(formData.role === 'repetiteur' || formData.role === 'les_deux') && formData.filiere && subjectsByFiliere[formData.filiere] && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-book text-[#002c46] mr-2"></i>
                    Matières que vous pouvez enseigner *
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {subjectsByFiliere[formData.filiere].map(subject => (
                      <label key={subject} className="flex items-center p-3 border border-gray-300 rounded-lg hover:bg-blue-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.matieres.includes(subject)}
                          onChange={() => handleSubjectToggle(subject)}
                          className="h-4 w-4 text-[#002c46] focus:ring-[#003d5c] border-gray-300 rounded mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">{subject}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact - pour tous */}
              {formData.role && (
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-phone text-[#002c46] mr-2"></i>
                    Téléphone (optionnel)
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                    placeholder="+41 XX XXX XX XX"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="fas fa-info-circle mr-1"></i>
                    Votre email de connexion sera utilisé comme email de contact
                  </p>
                </div>
              )}

              {/* Tarif - pour tuteurs - MASQUÉ */}
              {false && (formData.role === 'repetiteur' || formData.role === 'les_deux') && (
                <div>
                  <label htmlFor="tarif" className="block text-sm font-semibold text-gray-700 mb-2">
                    <i className="fas fa-money-bill text-[#002c46] mr-2"></i>
                    Tarif horaire (CHF)
                  </label>
                  <input
                    type="number"
                    id="tarif"
                    name="tarif"
                    min="25"
                    max="60"
                    value={formData.tarif}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                  />
                  <p className="text-xs text-gray-500 mt-1">Entre 25 et 60 CHF par heure</p>
                </div>
              )}

              {/* Tarif et autres champs tuteur - masqués */}
              </>
              )}

              {/* Conditions d'utilisation - TOUJOURS VISIBLE */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="acceptTerms"
                    name="acceptTerms"
                    type="checkbox"
                    required
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    className="h-4 w-4 text-[#002c46] focus:ring-[#003d5c] border-gray-300 rounded"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="acceptTerms" className="text-gray-700">
                    J'accepte les{' '}
                    <Link to="/conditions" className="text-[#002c46] hover:text-[#001a2a] underline">
                      conditions d'utilisation
                    </Link>
                    {' '}et la{' '}
                    <Link to="/confidentialite" className="text-[#002c46] hover:text-[#001a2a] underline">
                      politique de confidentialité
                    </Link>
                    *
                  </label>
                </div>
              </div>

              {/* hCaptcha - Protection anti-bot */}
              <div className="space-y-3">
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  captchaToken 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 bg-gray-50'
                }`}>
                  {!captchaToken && (
                    <HCaptcha
                      onVerify={(token) => {
                        setCaptchaToken(token);
                      }}
                      onExpire={() => {
                        setCaptchaToken('');
                      }}
                      onError={() => {
                        setCaptchaToken('');
                        showToast('Erreur de vérification. Veuillez réessayer.', 'error');
                      }}
                    />
                  )}
                  
                  {captchaToken ? (
                    <div className="text-center py-4">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-3">
                        <i className="fas fa-check text-white text-3xl"></i>
                      </div>
                      <p className="text-lg font-bold text-green-700 mb-1">
                        ✅ Vérification réussie !
                      </p>
                      <p className="text-sm text-green-600">
                        Vous pouvez maintenant continuer l'inscription
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-600 text-center mt-2">
                      <i className="fas fa-shield-alt mr-1"></i>
                      Protection contre les robots automatiques
                    </p>
                  )}
                </div>
              </div>

              {/* Bouton d'inscription */}
              <button
                type="submit"
                disabled={isSubmitting || !captchaToken}
                className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] focus:ring-2 focus:ring-[#003d5c] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!captchaToken ? 'Veuillez valider le captcha pour continuer' : ''}
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Création en cours...
                  </>
                ) : !captchaToken ? (
                  <>
                    <i className="fas fa-lock mr-2"></i>
                    Validez le captcha pour continuer
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus mr-2"></i>
                    Créer mon profil complet
                  </>
                )}
              </button>
              {!captchaToken && !isSubmitting && (
                <p className="text-xs text-orange-600 text-center mt-2">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  Le bouton sera activé après validation du captcha
                </p>
              )}
            </form>

            {/* Divider */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Vous avez déjà un compte ?
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center w-full py-3 px-4 border border-[#002c46] text-[#002c46] font-semibold rounded-lg hover:bg-[#002c46]/5 transition-colors"
                >
                  <i className="fas fa-sign-in-alt mr-2"></i>
                  Se connecter
                </Link>
              </div>
            </div>

            {/* Retour */}
            <div className="mt-6 text-center">
              <BackButton />
            </div>
          </div>
        </div>
      </div>
  );
};
